import { useCallback, useRef, useEffect } from 'react';
import { ContextualToolbar } from '../Toolbars/ContextualToolbar';
import { FormattingToolbar } from '../Toolbars/FormattingToolbar';
import { useEditorHistory } from '../../hooks/useEditorHistory';
import { useEditorFormatting } from '../../hooks/useEditorFormatting';
import { markdownToHtml, htmlToMarkdown } from '../../utils/markdownConverter';
import styles from './RichTextEditor.module.css';

interface RichTextEditorProps {
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
}

export const RichTextEditor = ({
	value,
	onChange,
	placeholder = 'Escreva o conteúdo da política...',
}: RichTextEditorProps) => {
	const editableRef = useRef<HTMLDivElement | null>(null);
	const isLockedRef = useRef(false);

	const { saveToHistory, undo, redo, canUndo, canRedo } = useEditorHistory(value);
	const { applyFormat, clearFormat, indent, outdent } = useEditorFormatting();

	useEffect(() => {
		const newHtml = markdownToHtml(value);
		const currentHtml = editableRef.current?.innerHTML || '';

		const normalizedNew = newHtml.replace(/\s+/g, '').trim();
		const normalizedCurrent = currentHtml.replace(/\s+/g, '').trim();

		if (normalizedNew !== normalizedCurrent && !isLockedRef.current) {
			if (editableRef.current) {
				editableRef.current.innerHTML = newHtml;
			}
		} else if (!value && !currentHtml) {
			const emptyState = '<p><br></p>';
			if (editableRef.current) {
				editableRef.current.innerHTML = emptyState;
			}
		}
	}, [value]);

	const handleInput = useCallback(() => {
		if (!editableRef.current) return;

		isLockedRef.current = true;

		const html = editableRef.current.innerHTML;
		const markdown = htmlToMarkdown(html);

		if (markdown !== value) {
			onChange(markdown);
			saveToHistory(markdown);
		}

		setTimeout(() => {
			isLockedRef.current = false;
		}, 50);
	}, [onChange, saveToHistory, value]);

	const handleUndo = useCallback(() => {
		const previousValue = undo();
		if (previousValue !== null) {
			onChange(previousValue);
		}
	}, [undo, onChange]);

	const handleRedo = useCallback(() => {
		const nextValue = redo();
		if (nextValue !== null) {
			onChange(nextValue);
		}
	}, [redo, onChange]);

	const handleFormat = useCallback(
		(syntax: string) => {
			const selection = globalThis.getSelection();
			const selectedText = selection?.toString() || '';

			if (
				applyFormat({
					syntax,
					selectedText,
					editableRef,
				})
			) {
				handleInput();
			}
		},
		[applyFormat, handleInput]
	);

	const handleClearFormat = useCallback(() => {
		if (clearFormat(editableRef)) {
			handleInput();
		}
	}, [clearFormat, handleInput]);

	const handleIndent = useCallback(() => {
		if (indent(editableRef)) {
			handleInput();
		}
	}, [indent, handleInput]);

	const handleOutdent = useCallback(() => {
		if (outdent(editableRef)) {
			handleInput();
		}
	}, [outdent, handleInput]);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Tab') {
				e.preventDefault();
				if (e.shiftKey) {
					handleOutdent();
				} else {
					handleIndent();
				}
				return;
			}

			if (e.ctrlKey || e.metaKey) {
				if (e.key === 'z' && !e.shiftKey) {
					e.preventDefault();
					handleUndo();
				} else if (e.key === 'y' || (e.key === 'z' && e.shiftKey)) {
					e.preventDefault();
					handleRedo();
				}
			}
		};

		const editable = editableRef.current;
		if (editable) {
			editable.addEventListener('keydown', handleKeyDown);
			return () => editable.removeEventListener('keydown', handleKeyDown);
		}
	}, [handleUndo, handleRedo, handleIndent, handleOutdent]);

	return (
		<div className={styles.container}>
			<FormattingToolbar
				onFormat={handleFormat}
				onUndo={handleUndo}
				onRedo={handleRedo}
				onClearFormat={handleClearFormat}
				onIndent={handleIndent}
				onOutdent={handleOutdent}
				canUndo={canUndo}
				canRedo={canRedo}
			/>

			<ContextualToolbar
				onFormat={handleFormat}
				onUndo={handleUndo}
				onRedo={handleRedo}
				onClearFormat={handleClearFormat}
				onIndent={handleIndent}
				onOutdent={handleOutdent}
				canUndo={canUndo}
				canRedo={canRedo}
			/>

			<div
				ref={editableRef}
				className={styles.editableArea}
				contentEditable
				onInput={handleInput}
				data-placeholder={placeholder}
				suppressContentEditableWarning
				spellCheck
				autoCorrect="on"
				autoCapitalize="sentences"
				inputMode="text"
				enterKeyHint="enter"
			/>
		</div>
	);
};
